// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * "Route approved work" — creates a work order in the external WOMS, at a site.
 *
 * Only `title` is required upstream. `provider` and `business_owner` are
 * searched and picked from the directories the WOMS exposes (they are created
 * there, not here), so an unknown value cannot be submitted; a 400 from
 * upstream is still surfaced in the modal rather than closing over it.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  ComboBox,
  InlineNotification,
  Modal,
  Select,
  SelectItem,
  TextArea,
  TextInput,
} from '@carbon/react';
import { useCreateWorkOrder } from '../../hooks/useWorkOrderMutation';
import { useBusinessOwners, useProviders } from '../../hooks/useWorkOrderDirectory';
import type { WorkOrderSite } from '../../services/workOrderService';
import type {
  BusinessOwner,
  Provider,
  WorkOrder,
  WorkOrderCreateRequest,
  WorkOrderPriority,
} from '../../types';

const PRIORITIES: WorkOrderPriority[] = ['NONE', 'LOW', 'MEDIUM', 'HIGH'];

const providerLabel = (p: Provider | null) => p?.name ?? '';
export const ownerLabel = (o: BusinessOwner | null) =>
  o ? (o.email ? `${o.name} (${o.email})` : o.name) : '';

/** Filter for a select-with-search. */
export function searchFilter<T>(selectedLabel: string) {
  return ({
    item,
    itemToString,
    inputValue,
  }: {
    item: T;
    itemToString?: (_item: T | null) => string;
    inputValue: string | null;
  }): boolean => {
    const query = inputValue?.trim().toLowerCase() ?? '';
    if (!query || inputValue === selectedLabel) return true;
    return (itemToString?.(item) ?? '').toLowerCase().includes(query);
  };
}

interface Props {
  open: boolean;
  /** The site the order is created at; null (whole fleet) uses the deprecated unscoped route. */
  siteId: WorkOrderSite;
  onClose: () => void;
  /** Receives the created work order, so a caller can select it or show its reference. */
  onCreated: (_workOrder: WorkOrder) => void;
  /**
   * Seed values, for callers raising a work order from something that already
   * describes the work — an alarm, say. Applied each time the modal opens, so
   * reopening from a different source re-seeds rather than showing the last one.
   */
  initial?: Partial<WorkOrderCreateRequest>;
}

export default function WorkOrderCreateModal({ open, siteId, onClose, onCreated, initial }: Props) {
  const [title, setTitle] = useState('');
  const [provider, setProvider] = useState('');
  const [businessOwner, setBusinessOwner] = useState('');
  const [originatingTask, setOriginatingTask] = useState('');
  const [expectedCompletion, setExpectedCompletion] = useState('');
  const [priority, setPriority] = useState<WorkOrderPriority>('NONE');
  const [description, setDescription] = useState('');

  const seed = () => {
    setTitle(initial?.title ?? '');
    setProvider(initial?.provider ?? '');
    setBusinessOwner(initial?.business_owner ?? '');
    setOriginatingTask(initial?.originating_task ?? '');
    setExpectedCompletion(initial?.expected_completion?.slice(0, 10) ?? '');
    setPriority(initial?.priority ?? 'NONE');
    setDescription(initial?.description ?? '');
  };

  useEffect(() => {
    if (open) seed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The follow-up runs at the hook level so closing or unmounting this modal
  // while the WOMS is still answering cannot drop it (see the hook).
  const create = useCreateWorkOrder(siteId, { onSuccess: workOrder => onCreated(workOrder) });
  const { providers, loading: providersLoading, error: providersError } = useProviders(open);
  const { owners, loading: ownersLoading, error: ownersError } = useBusinessOwners(open);
  const selectedProvider =
    providers.find(p => p.name === provider) ?? (provider ? { id: '', name: provider } : null);
  const selectedOwner = owners.find(o => o.id === businessOwner) ?? null;

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (create.isError)
      errorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [create.isError, create.error]);

  const reset = () => {
    seed();
    create.reset();
  };

  // Not while the request is in flight: the order may already exist upstream
  // by the time the modal would close, and the caller's follow-up (an alarm
  // link, an event) needs the result. Carbon disables both buttons through
  // `loadingStatus`; this covers Escape and the close button. A ref, because
  // Carbon binds its Escape listener once per open and would otherwise call
  // the handler from before the request started.
  const inFlight = useRef(false);
  inFlight.current = create.isPending;
  const handleClose = () => {
    if (inFlight.current) return;
    reset();
    onClose();
  };

  const handleSubmit = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const request: WorkOrderCreateRequest = { title: trimmed };
    if (provider) request.provider = provider;
    if (businessOwner) request.business_owner = businessOwner;
    if (originatingTask.trim()) request.originating_task = originatingTask.trim();
    if (expectedCompletion) request.expected_completion = `${expectedCompletion}T00:00:00Z`;
    if (priority !== 'NONE') request.priority = priority;
    if (description.trim()) request.description = description.trim();
    if (initial?.source_alarm) request.source_alarm = initial.source_alarm;
    if (initial?.extra_fields) request.extra_fields = initial.extra_fields;

    create.mutate(request, { onSuccess: () => reset() });
  };

  return (
    <Modal
      open={open}
      modalHeading="Route approved work"
      primaryButtonText="Route to provider"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={title.trim().length === 0}
      loadingStatus={create.isPending ? 'active' : 'inactive'}
      loadingDescription="Routing…"
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      preventCloseOnClickOutside
      size="sm"
    >
      <p className="wo-modal-note">
        This creates the order in the provider&apos;s own system. Helios keeps no copy — what you
        see afterwards is whatever that system stored.
      </p>

      {create.isPending && (
        <InlineNotification
          kind="info"
          title="Routing in progress"
          subtitle="This window stays open until the provider answers, so the order is never created without Helios knowing."
          lowContrast
          hideCloseButton
          className="wo-modal-error"
        />
      )}

      {create.isError && (
        <div ref={errorRef}>
          <InlineNotification
            kind="error"
            title="Could not route work"
            subtitle={create.error.message}
            lowContrast
            hideCloseButton
            className="wo-modal-error"
          />
        </div>
      )}

      <TextInput
        id="wo-title"
        labelText="Title (required)"
        placeholder="e.g. West cluster cooling inspection"
        value={title}
        onChange={e => setTitle(e.target.value)}
      />
      <ComboBox<Provider>
        id="wo-provider"
        titleText="External provider"
        placeholder={providersLoading ? 'Loading providers…' : 'Search or type a provider name'}
        disabled={providersLoading}
        helperText="Not listed? Type the provider's exact name."
        allowCustomValue
        items={providers}
        itemToString={providerLabel}
        shouldFilterItem={searchFilter<Provider>(providerLabel(selectedProvider))}
        selectedItem={selectedProvider}
        // Carbon commits a list pick as the item, a typed value on blur as a
        // string, and a typed value on Enter as null plus inputValue.
        onChange={({ selectedItem, inputValue }) => {
          const picked = selectedItem as Provider | string | null | undefined;
          setProvider(
            (typeof picked === 'string' ? picked : (picked?.name ?? inputValue ?? '')).trim()
          );
        }}
        warn={!!providersError}
        warnText={`Could not load providers: ${providersError}. Type the provider's exact name instead.`}
      />
      <ComboBox<BusinessOwner>
        id="wo-owner"
        titleText="Business owner"
        placeholder={ownersLoading ? 'Loading business owners…' : 'Search by name or email'}
        disabled={ownersLoading}
        items={owners}
        itemToString={ownerLabel}
        shouldFilterItem={searchFilter<BusinessOwner>(ownerLabel(selectedOwner))}
        selectedItem={selectedOwner}
        onChange={({ selectedItem }) => setBusinessOwner(selectedItem?.id ?? '')}
        invalid={!!ownersError}
        invalidText={`Could not load business owners: ${ownersError}`}
      />
      <TextInput
        id="wo-task"
        labelText="Originating approved task"
        helperText="Recorded for correlation; not a validated link"
        placeholder="e.g. PLN-4821"
        value={originatingTask}
        onChange={e => setOriginatingTask(e.target.value)}
      />
      <TextInput
        id="wo-due"
        labelText="Expected completion"
        type="date"
        value={expectedCompletion}
        onChange={e => setExpectedCompletion(e.target.value)}
      />
      <Select
        id="wo-priority"
        labelText="Priority"
        value={priority}
        onChange={e => setPriority(e.target.value as WorkOrderPriority)}
      >
        {PRIORITIES.map(p => (
          <SelectItem key={p} value={p} text={p} />
        ))}
      </Select>
      <TextArea
        id="wo-description"
        labelText="Detail for the provider"
        rows={3}
        value={description}
        onChange={e => setDescription(e.target.value)}
      />
    </Modal>
  );
}
